import { useEffect, useState } from 'react';
import { Vote, Lock, X as CloseIcon, Inbox } from 'lucide-react';
import { api, timeAgo } from '../api.js';
import { UserChip } from '../components/UserChip.jsx';
import { MentionText } from '../components/MentionText.jsx';
import { useGuild } from './GuildLayout.jsx';

function bar(pct) {
  const len = 14;
  const filled = Math.round((pct / 100) * len);
  return '█'.repeat(filled) + '░'.repeat(len - filled);
}

export default function Polls() {
  const { guildId } = useGuild();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => { reload(); }, [guildId]);

  function reload() {
    setData(null);
    api.listPolls(guildId).then(setData).catch(err => setData({ error: err.message }));
  }

  function fireToast(type, msg) { setToast({ type, msg }); setTimeout(() => setToast(null), 2500); }

  async function toggleExpand(id) {
    if (expanded === id) { setExpanded(null); setExpandedDetail(null); return; }
    setExpanded(id);
    try {
      const detail = await api.getPoll(guildId, id);
      setExpandedDetail(detail);
    } catch (err) {
      fireToast('error', err.message);
    }
  }

  async function close(id) {
    if (!confirm('Close this poll? Buttons will be disabled and the embed marked closed.')) return;
    try {
      await api.closePoll(guildId, id);
      reload();
      fireToast('success', 'Poll closed');
    } catch (err) { fireToast('error', err.message); }
  }

  if (!data) return <div className="card"><div className="skel skel-line med" /><div className="skel skel-line short" /></div>;
  if (data.error) return <div className="empty">Error: {data.error}</div>;

  return (
    <>
      <h1>Polls</h1>
      <p className="subtitle">Create new polls with <span className="tag">/poll create</span> in any channel. This page lists all polls in this server with their live results.</p>

      {!data.polls.length ? (
        <div className="card empty">
          <div className="ico"><Inbox size={32} strokeWidth={1.5} /></div>
          <p>No polls yet. Try <span className="tag">/poll create question:What's for dinner? options:Pizza, Tacos, Burgers</span></p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
          {data.polls.map(p => {
            const isOpen = !p.closed_at;
            const total = p.total_votes;
            const showDetail = expanded === p.id;
            return (
              <div className="card" key={p.id}>
                <div className="row" style={{ alignItems: 'flex-start', cursor: 'pointer' }} onClick={() => toggleExpand(p.id)}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>
                      <Vote size={14} strokeWidth={2.5} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                      <MentionText text={p.question} chipSize={16} />
                    </div>
                    <div className="muted" style={{ fontSize: 13, marginTop: 4, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className={`badge ${isOpen ? 'success' : ''}`}>{isOpen ? 'open' : 'closed'}</span>
                      <span>#{p.id}</span>
                      <span>·</span>
                      <span>{total} vote{total === 1 ? '' : 's'}</span>
                      <span>·</span>
                      <span>started {timeAgo(p.created_at)}</span>
                      {p.closes_at && isOpen && <><span>·</span><span>auto-closes <span title={new Date(p.closes_at * 1000).toLocaleString()}>{timeAgo(p.closes_at)}</span></span></>}
                      {p.required_role_id && <><span>·</span><span className="badge warn">role-locked</span></>}
                      {p.anonymous && <><span>·</span><span className="badge">anonymous</span></>}
                      {p.multi_vote && <><span>·</span><span className="badge accent">multi-vote</span></>}
                    </div>
                  </div>
                  {isOpen && (
                    <div className="row" style={{ flex: 0 }}>
                      <button className="ghost danger-text" onClick={(e) => { e.stopPropagation(); close(p.id); }}>
                        <CloseIcon size={13} strokeWidth={2} /> Close
                      </button>
                    </div>
                  )}
                </div>

                {showDetail && expandedDetail && expandedDetail.poll.id === p.id && (
                  <div style={{ marginTop: '1rem', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                    {p.options.map((opt, i) => {
                      const c = expandedDetail.counts[i] || 0;
                      const pct = total ? (c / total) * 100 : 0;
                      return (
                        <div key={i} style={{ marginBottom: '.6rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, marginBottom: 2 }}>
                            <span><strong>{i + 1}.</strong> <MentionText text={opt} chipSize={18} /></span>
                            <span className="muted">{c} · {pct.toFixed(0)}%</span>
                          </div>
                          <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, color: 'var(--accent-2)' }}>{bar(pct)}</div>
                        </div>
                      );
                    })}
                    {expandedDetail.voters && expandedDetail.voters.length > 0 && (
                      <div style={{ marginTop: '1rem' }}>
                        <div className="dim" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700, marginBottom: 6 }}>Voters</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
                          {expandedDetail.voters.map(v => (
                            <span key={v.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '2px 8px', background: 'var(--bg-elev-2)', borderRadius: 4 }}>
                              <UserChip guildId={guildId} userId={v.user_id} size={16} link={false} />
                              <span className="dim">· #{v.option_index + 1}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {expandedDetail.voters === null && (
                      <div className="muted" style={{ fontSize: 12, marginTop: '.75rem' }}>
                        <Lock size={11} strokeWidth={2.5} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                        Anonymous poll — voter names are hidden.
                      </div>
                    )}
                    {p.message_id && (
                      <div style={{ marginTop: '.75rem' }}>
                        <a href={`https://discord.com/channels/${guildId}/${p.channel_id}/${p.message_id}`} target="_blank" rel="noreferrer">Jump to poll in Discord →</a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}
